// A tab that was opened before a deploy keeps running the old bundle — for
// days. Nothing ever reloads it: the idle logout and the login after it are
// client-side navigations, and Cloudflare Pages keeps serving a previous
// deployment's asset hashes for a while, so the stale code does not even
// break. It just behaves like the old release. Found on 2026-09-10: Edda Nar
// re-logged in two hours after the /offers/<id> fix went live and still saw
// the list instead of the offer, because her tab had loaded before the deploy.
//
// Every build stamps an id into the bundle (__APP_BUILD_ID__) and into
// dist/version.json (see vite.config.ts). The tab asks for version.json now
// and then; once the server announces a different build, the NEXT page change
// is done as a full load instead of a client-side one. That is the one moment
// a reload costs almost nothing: a page change unmounts the page anyway.
// What does survive a page change — a Zoki conversation in progress, PDF
// translations still uploading in the background — registers itself with
// setReloadBusy(), and the reload waits for a later page change.

import type { AnyRouter } from '@tanstack/react-router';

declare const __APP_BUILD_ID__: string | undefined;

export const APP_BUILD_ID: string =
  typeof __APP_BUILD_ID__ === 'string' && __APP_BUILD_ID__ ? __APP_BUILD_ID__ : 'dev';

/** sessionStorage key: the server build this tab already reloaded for. */
const ATTEMPT_KEY = '4m.version.reloadedFor';
const CHECK_INTERVAL_MS = 5 * 60_000;
const MIN_GAP_MS = 60_000;

/**
 * Pure decision: should the next page change be a full load?
 *
 * - `dev` never reloads (no version.json in the dev server).
 * - Unknown server build → no.
 * - Same build → no.
 * - Different build, but this tab already reloaded for exactly that build
 *   and is STILL on another one → no. The reload did not help (a stale
 *   index.html somewhere between us and the server); reloading on every
 *   click from then on would be worse than running the old code.
 */
export function shouldReload(input: {
  running: string;
  server: string | null;
  alreadyReloadedFor: string | null;
}): boolean {
  const { running, server, alreadyReloadedFor } = input;
  if (running === 'dev') return false;
  if (!server || server === running) return false;
  if (alreadyReloadedFor === server) return false;
  return true;
}

/** Reads the build id out of a version.json body; anything else is null. */
export function parseVersionBody(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const build = (body as { build?: unknown }).build;
  return typeof build === 'string' && build.length > 0 && build.length <= 100 ? build : null;
}

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* private mode — the loop guard is then per page load, still bounded */
  }
}

let serverBuild: string | null = null;
let lastCheckAt = 0;
let inFlight: Promise<void> | null = null;

export function checkForUpdate(force = false): Promise<void> {
  if (APP_BUILD_ID === 'dev') return Promise.resolve();
  const now = Date.now();
  if (!force && now - lastCheckAt < MIN_GAP_MS) return inFlight ?? Promise.resolve();
  if (inFlight) return inFlight;
  lastCheckAt = now;
  inFlight = (async () => {
    try {
      // no-store: a cached version.json would announce the build we already
      // have and hide the new one.
      const res = await fetch('/version.json', { cache: 'no-store', credentials: 'omit' });
      if (!res.ok) return;
      // Pages answers an unknown path with index.html and a 200, so the body
      // is only trusted when it parses into the expected shape.
      const build = parseVersionBody(await res.json().catch(() => null));
      if (build) serverBuild = build;
    } catch {
      /* offline or blocked — try again at the next trigger */
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function isStale(): boolean {
  return shouldReload({
    running: APP_BUILD_ID,
    server: serverBuild,
    alreadyReloadedFor: readSession(ATTEMPT_KEY),
  });
}

// ── Work that a client-side page change keeps alive and a reload would not ──
const busy = new Set<string>();

/** Hold off the version reload while `on`; release with `on = false`. */
export function setReloadBusy(key: string, on: boolean): void {
  if (on) busy.add(key);
  else busy.delete(key);
}

let reloadingForUpdate = false;

/** True once this document is being replaced by a newer build — the unload
 *  that follows is not a logout and not a page view. */
export function isReloadingForUpdate(): boolean {
  return reloadingForUpdate;
}

/**
 * Wire the reload into the router. Separate from installVersionWatcher so it
 * can be tested against a real router without a browser.
 *
 * It listens to `onBeforeLoad`, NOT `onBeforeNavigate`: router-core 1.169
 * stops emitting onBeforeNavigate for good once any beforeLoad redirect has
 * happened in the document (it keeps the redirect in a store that nothing
 * clears) — and a redirect happens on every expired-session open (_app →
 * /login) and every plain /login sign-in (/login → /dashboard). Listening to
 * onBeforeNavigate, the watcher would have been dead in exactly those tabs.
 */
export function attachReloadOnPageChange(
  router: AnyRouter,
  deps: {
    stale: () => boolean;
    canReload: () => boolean;
    reload: (href: string) => void;
  },
): () => void {
  return router.subscribe('onBeforeLoad', (event) => {
    // Only a change of page, never a changed filter or search param — those
    // unmount nothing, and reloading there would throw away state.
    if (!event.pathChanged || !deps.stale() || !deps.canReload()) return;
    deps.reload(event.toLocation.href);
  });
}

let installed = false;

export function installVersionWatcher(router: AnyRouter): void {
  if (installed || APP_BUILD_ID === 'dev' || typeof window === 'undefined') return;
  installed = true;

  void checkForUpdate(true);
  window.setInterval(() => void checkForUpdate(), CHECK_INTERVAL_MS);
  // Coming back to a tab is the typical moment after a deploy: the person
  // was away, the release went out, and the next click should get it.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  });
  window.addEventListener('focus', () => void checkForUpdate());

  attachReloadOnPageChange(router, {
    stale: isStale,
    // Offline, a reload lands on the browser's error page. Busy, it would
    // throw away work a client-side page change keeps. Either way the next
    // page change tries again — nothing is recorded when a reload is skipped.
    canReload: () => navigator.onLine !== false && busy.size === 0,
    reload: (href) => {
      writeSession(ATTEMPT_KEY, serverBuild ?? '');
      reloadingForUpdate = true;
      // History already points at the target by the time this fires, so this
      // loads exactly the page that was asked for, in the new build.
      window.location.replace(href);
    },
  });

  // A chunk that failed to load MAY mean a deploy pruned it. Deliberately no
  // reload here: this also fires for a hover preload (defaultPreload:
  // 'intent') or an in-page import like the PDF generator, and reloading then
  // would wipe whatever the person was typing. Just look for a new build now,
  // so the next page change can pick it up; the error itself still reaches
  // its caller and TanStack's own retry.
  window.addEventListener('vite:preloadError', () => void checkForUpdate(true));
}
